/**
 * LexisNexis Page
 *
 * Query editor with validation, preview, and data source registration.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useLexisNexisSources, useLexisNexisMutations } from '../../api/lexisnexis';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import type { LexisNexisValidation, LexisNexisPreview } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';
import { DataExplorer } from '../explorer';

export function LexisNexisPage() {
  const { theme } = useTheme();
  const { selectedProject } = useHierarchy();
  const { data: sources, loading, error, refetch } = useLexisNexisSources(selectedProject?.id);
  const { validate, preview, register } = useLexisNexisMutations();

  const [query, setQuery] = useState('');
  const [validation, setValidation] = useState<LexisNexisValidation | null>(null);
  const [previewData, setPreviewData] = useState<LexisNexisPreview | null>(null);
  // Changes per preview so the explorer resets its per-sample state.
  const [previewKey, setPreviewKey] = useState('');
  const [validating, setValidating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // The data window is configured on the project (Projects page), so preview
  // uses the selected project's start_ts/end_ts. A pubDate clause is not valid
  // in the stored/validated query, so strip it if a user pastes one in.
  const startDate = selectedProject?.start_ts ? String(selectedProject.start_ts).slice(0, 10) : '';
  const endDate = selectedProject?.end_ts ? String(selectedProject.end_ts).slice(0, 10) : '';
  const cleanQuery = (q: string) =>
    q
      .replace(/\s*(?:AND\s+)?\(?\s*pubDate\s*:\s*\[[^\]]*\]\s*\)?/gi, '')
      .replace(/\(\s*\)/g, '')
      .replace(/\s+(AND|OR)\s*$/i, '')
      .replace(/^\s*(AND|OR)\s+/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const handleValidate = async () => {
    const q = cleanQuery(query);
    if (!q) return;
    setValidating(true);
    setErrorMsg('');
    setValidation(null);
    setPreviewData(null);
    try {
      const result = await validate(q, startDate || undefined, endDate || undefined);
      if (result) setValidation(result);
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Validation failed.'));
    } finally {
      setValidating(false);
    }
  };

  const handlePreview = async () => {
    const q = cleanQuery(query);
    if (!q) return;
    setPreviewing(true);
    setErrorMsg('');
    // Clear first so a failed or slow preview is never mistaken for this query's results.
    setPreviewData(null);
    try {
      const result = await preview(q, startDate || undefined, endDate || undefined);
      if (result) {
        setPreviewData(result);
        setPreviewKey(`${q}|${Date.now()}`);
      }
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Preview failed.'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleRegister = async () => {
    const q = cleanQuery(query);
    if (!q || !selectedProject) return;
    setRegistering(true);
    setErrorMsg('');
    try {
      await register({
        query: q,
        id_project: selectedProject.id,
      });
      setQuery('');
      setValidation(null);
      setPreviewData(null);
      refetch();
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Registration failed.'));
    } finally {
      setRegistering(false);
    }
  };

  if (!selectedProject) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>LexisNexis</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                Select a client, matter, and project from the context bar to register LexisNexis sources.
              </span>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  if (loading) return <LoadingState message="Loading LexisNexis sources..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="lg">
      <div>
        <h1 style={{ ...textStyles.h3, color: textColor }}>LexisNexis</h1>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
          Project: {selectedProject.name} (v{selectedProject.version})
        </p>
      </div>

      {/* Query Editor */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <div>
              <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>
                Search Query
              </label>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter your LexisNexis search query..."
                rows={4}
                style={{
                  width: '100%',
                  padding: spacing.sm,
                  borderRadius: '6px',
                  border: `1px solid ${borderColor}`,
                  background: theme.colors.background.paper,
                  color: textColor,
                  fontSize: fontSize.sm,
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
              />
            </div>

            <span style={{ color: mutedColor, fontSize: fontSize.xs }}>
              Enter the query without a pubDate clause. The date window comes from the
              project ({startDate || '—'} – {endDate || 'ongoing'}); set it on the Projects page.
            </span>

            <RoleGate minRole={GATES.datasourceWrite}>
              <Flex gap="sm">
                <Button variant="outline" size="sm" onClick={handleValidate} disabled={!cleanQuery(query) || validating}>
                  {validating ? 'Validating...' : 'Validate'}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePreview} disabled={!cleanQuery(query) || !validation?.valid || previewing}>
                  {previewing ? 'Loading...' : 'Preview'}
                </Button>
                <Button variant="solid" size="sm" onClick={handleRegister} disabled={!cleanQuery(query) || !validation?.valid || registering}>
                  {registering ? 'Registering...' : 'Register Source'}
                </Button>
              </Flex>
            </RoleGate>

            {errorMsg && (
              <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.sm }}>{errorMsg}</span>
            )}

            {/* Validation Result */}
            {validation && (
              <Card variant="outlined" padding="sm">
                <Flex gap="md" align="center">
                  <span style={{
                    fontSize: fontSize.xs,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: validation.valid
                      ? theme.colors.semantic.success.light + '30'
                      : theme.colors.semantic.error.light + '30',
                    color: validation.valid
                      ? theme.colors.semantic.success.default
                      : theme.colors.semantic.error.default,
                    fontWeight: 600,
                  }}>
                    {validation.valid ? 'Valid' : 'Invalid'}
                  </span>
                  <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                    Articles: {validation.article_count.toLocaleString()}
                  </span>
                  <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                    Rate limit remaining: {validation.rate_limit_remaining}
                  </span>
                </Flex>
                {validation.errors && validation.errors.length > 0 && (
                  <div style={{ marginTop: spacing.xs }}>
                    {validation.errors.map((err, i) => (
                      <div key={i} style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.xs }}>{err}</div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* Preview Results */}
      {previewData && (
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="sm">
              <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
                Preview
              </span>
              {previewData.articles.length === 0 ? (
                <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                  No articles match this query in the project's date window
                  ({previewData.total_count.toLocaleString()} total).
                </span>
              ) : (
                <>
                  {previewData.total_count > previewData.articles.length && (
                    <span style={{ color: mutedColor, fontSize: fontSize.xs }}>
                      Showing the first {previewData.articles.length.toLocaleString()} of{' '}
                      {previewData.total_count.toLocaleString()} matching articles.
                    </span>
                  )}
                  <DataExplorer
                    rows={previewData.articles}
                    totalCount={previewData.total_count}
                    scope="lexisnexis-preview"
                    exportName="lexisnexis-preview"
                    sampleKey={previewKey}
                  />
                </>
              )}
            </Stack>
          </Card.Body>
        </Card>
      )}

      {/* Registered Sources */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="sm">
            <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
              Registered Sources
            </span>
            {sources && sources.length > 0 ? (
              <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
                {sources.map((source) => (
                  <div key={source.id} style={{ padding: `${spacing.sm} ${spacing.md}`, borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ fontFamily: 'monospace', fontSize: fontSize.xs, color: textColor }}>{source.query}</div>
                    <Flex gap="sm" style={{ marginTop: '2px' }}>
                      <span style={{ color: mutedColor, fontSize: fontSize.xs }}>
                        Articles: {source.article_count.toLocaleString()}
                      </span>
                      <span style={{
                        fontSize: '0.7rem',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        background: source.active ? theme.colors.semantic.success.light + '30' : theme.colors.background.paper,
                        color: source.active ? theme.colors.semantic.success.default : mutedColor,
                      }}>
                        {source.active ? 'Active' : 'Inactive'}
                      </span>
                    </Flex>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>No sources registered yet.</span>
            )}
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}
